import {
  Button,
  Container,
  Field,
  Flex,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  Spacer,
  Text,
} from "@chakra-ui/react"
import {
  Link,
  createFileRoute,
  redirect,
} from "@tanstack/react-router"
import { type SubmitHandler, useForm } from "react-hook-form"
import { FiEye, FiEyeOff } from "react-icons/fi"
import { useToggle } from "react-use"

import Logo from "../../public/assets/images/logo-hermes.svg"
import type { Body_authentication_login_access_token as AccessToken } from "../client"
import useAuth from "../hooks/useAuth"
import { emailPattern, getPolicyPublic } from "../utils"
import { useQuery } from "@tanstack/react-query"

export const Route = createFileRoute("/login")({
  component: Login,
  beforeLoad: async () => {
    const token = localStorage.getItem("access_token")
    if (token) {
      throw redirect({
        to: "/",
      })
    }
  },
})

function Login() {
  const {data: policy} = useQuery({
    ...getPolicyPublic()
  })

  const [show, setShow] = useToggle(false)
  const { loginMutation, error, resetError } = useAuth()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AccessToken>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      username: "",
      password: "",
    },
  })

  const onSubmit: SubmitHandler<AccessToken> = async (data) => {
    if (isSubmitting) return

    resetError()

    try {
      await loginMutation.mutateAsync(data)
    } catch {
      // error is handled by useAuth hook
    }
  }

  return (
    <>
      <Container
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        h="95vh"
        justifyContent="center"
        gap={4}
        centerContent
      >
        <Image
          src={policy?.logo ? policy.logo.path.replace(/-(LG)\./, "-MD.") : Logo}
          alt="Logo"
          maxW="15em"
          mb={4}
        />
        <Text fontSize="5xl">
          Redaktionssystem
        </Text>
        <Spacer maxH="2em" />
        <Container maxW="sm" alignItems="stretch" gap={4} centerContent textAlign="center">
          <Field.Root id="username" invalid={!!errors.username || !!error}>
            <Input
              id="username"
              {...register("username", {
                required: "Benutzername ist erforderlich",
                pattern: emailPattern,
              })}
              placeholder="E-Mail"
              type="email"
              required
            />
            {errors.username && (
              <Field.ErrorText>{errors.username.message}</Field.ErrorText>
            )}
          </Field.Root>
          <Field.Root id="password" invalid={!!error}>
            <InputGroup
              css={{
                "& > div": {
                  padding: "0 !important",
                },
              }}
              endElement={
                <IconButton
                  onClick={setShow}
                  _dark={{
                    backgroundColor: "dark",
                  }}
                  color="dim"
                  backgroundColor="inherit"
                  cursor="pointer"
                  borderTopColor="inherit"
                  borderBottomColor="inherit"
                  borderRightColor="inherit"
                >
                  <Icon
                    as={show ? FiEyeOff : FiEye}
                    aria-label={
                      show ? "Passwort verstecken" : "Passwort anzeigen"
                    }
                  />
                </IconButton>
              }
            >
              <Input
                {...register("password", {
                  required: "Passwort ist erforderlich",
                })}
                type={show ? "text" : "password"}
                placeholder="Passwort"
                required
              />
            </InputGroup>
            {error && <Field.ErrorText>{error}</Field.ErrorText>}
          </Field.Root>
          <Button variant="primary" type="submit" loading={isSubmitting}>
            Anmelden
          </Button>
          <Spacer maxH="2em" />
          <Link to="/recover-password">
            <Text color="blue.500">Passwort vergessen?</Text>
          </Link>
        </Container>
      </Container>

      <Flex h="5vh" spaceX={8} fontSize={12} alignItems="center" justifyContent="center">
        <Link to="/datenschutz">Datenschutz</Link>
        <Link to="/impressum">Impressum</Link>
      </Flex>
    </>
  )
}
